import { useMemo } from 'react'

import type { ReadonlyRecord } from '../../../utils'

type MinimalClass<Dependencies extends readonly unknown[]> = new (...args: Dependencies) => any

type ExtensibleHook<Dependencies extends readonly unknown[], Class extends MinimalClass<Dependencies>> = {
    (): InstanceType<Class>

    /** Extend the class used to create this hook */
    extendClass<NewClass extends MinimalClass<Dependencies>>(extender: (baseClass: Class) => NewClass): NewClass

    /** Extend the hook with a new class used to create instances */
    extend<NewClass extends MinimalClass<Dependencies>>(extender: (baseClass: Class) => NewClass, name: string): ExtensibleHook<Dependencies, NewClass>
}

/**
 * Utility function to create extensible hooks, that can use class inheritance to reuse code
 */
export const createExtensibleHook = <Dependencies extends readonly unknown[], ClassConstructor extends MinimalClass<Dependencies>, Name extends string>(
    useDependencies: () => Dependencies,
    Class: ClassConstructor,
    name: Name
): ExtensibleHook<Dependencies, ClassConstructor> => {
    const namedFunctions = {
        [name]: () => {
            const dependencies = useDependencies()

            /** This memo ensures this hook can be used without causing additional re-renders */
            return useMemo(() => new Class(...dependencies), dependencies)
        },
    } as ReadonlyRecord<Name, ExtensibleHook<Dependencies, ClassConstructor>>
    const namedFunction = namedFunctions[name]

    namedFunction.extendClass = (extender) => extender(Class)
    namedFunction.extend = (extender, name) => createExtensibleHook(useDependencies, namedFunction.extendClass(extender), name)

    return namedFunction
}
